import React from "react";
import { Link } from "react-router-dom";
import {
  Wallet,
  ArrowRight,
  Check,
  Zap,
  PieChart,
  Lightbulb,
  TrendingUp,
  TrendingDown,
  Sparkles,
} from "lucide-react";
import FinlyLogo from "@/components/FinlyLogo";
import FinlyLogoMark from "@/components/FinlyLogoMark";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[#0F172A] font-body antialiased" data-testid="landing-page">
      {/* ────────────────────────────────────────── NAV ────────────────────────────────────────── */}
      <nav
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/80 border-b border-[#E2E8F0]"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <FinlyLogo size={54} rounded="18%" />
            <div className="flex flex-col leading-none">
              <span className="font-display text-[27px] font-semibold">Finly+</span>
              <span
                className="finly-tagline-animated inline-flex items-center gap-1.5 mt-1.5 text-[15px] font-medium text-[#5C6B3A]"
                data-testid="nav-tagline"
              >
                Tu dinero, en una
                <svg
                  className="finly-tagline-check w-[14px] h-[14px]"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#8A9A5B"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12l4.5 4.5L19 7" />
                </svg>
                <span className="finly-tagline-underline ml-1 w-6"></span>
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-base text-[#64748B]">
            <a href="#features" className="hover:text-[#0F172A] transition-colors">Funciones</a>
            <a href="#how" className="hover:text-[#0F172A] transition-colors">Cómo funciona</a>
            <a href="#preview" className="hover:text-[#0F172A] transition-colors">Vista previa</a>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/login"
              className="hidden sm:inline-flex text-base font-medium text-[#0F172A] hover:text-[#0052FF] transition-colors"
              data-testid="nav-login"
            >
              Iniciar sesión
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-1.5 h-11 px-5 bg-[#0F172A] hover:bg-[#0052FF] text-white text-base font-medium rounded-md transition-colors"
              data-testid="nav-cta-register"
            >
              Crear cuenta
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* ────────────────────────────────────────── HERO ────────────────────────────────────────── */}
      <section
        className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden finly-hero-gradient finly-hero-grid"
        data-testid="landing-hero"
      >
        <div className="finly-hero-orb" style={{ top: "5%", right: "-100px" }}></div>
        <div className="finly-hero-orb" style={{ bottom: "10%", left: "-120px", animationDelay: "5s" }}></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-6 text-white">
            <div className="mb-8">
              <FinlyLogoMark size={120} className="drop-shadow-2xl" />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-8">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#00E590] animate-pulse"></span>
              <span className="text-sm uppercase tracking-[0.25em] font-medium">
                Beta abierta · 10 días gratis
              </span>
            </div>

            <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.95] mb-8">
              Tu dinero,
              <br />
              <span className="text-[#00E590]">en una.</span>
            </h1>

            <p className="text-xl sm:text-2xl text-white/80 mb-10 max-w-xl leading-relaxed">
              Finly+ te ayuda a registrar ingresos y gastos, visualizar tus finanzas y recibir
              recomendaciones inteligentes. Sin hojas de cálculo, sin estrés.
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 h-14 px-7 bg-[#00E590] hover:bg-[#00C97F] text-[#0F172A] font-semibold text-base rounded-md transition-all hover:scale-[1.02]"
                data-testid="hero-cta-register"
              >
                Crear cuenta gratis
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 h-14 px-7 bg-white/10 hover:bg-white/20 text-white font-medium text-base rounded-md border border-white/30 backdrop-blur-sm transition-all"
                data-testid="hero-cta-login"
              >
                Ya tengo cuenta
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/70">
              <div className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[#00E590]" />
                Sin tarjeta de crédito
              </div>
              <div className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[#00E590]" />
                Datos cifrados
              </div>
              <div className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-[#00E590]" />
                Cancela cuando quieras
              </div>
            </div>
          </div>

          {/* Right: dashboard mockup */}
          <div className="lg:col-span-6 relative">
            <DashboardMockup compact />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────── FEATURES ────────────────────────────────────────── */}
      <section id="features" className="py-24 lg:py-32 bg-[#FAFAFA]" data-testid="landing-features">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-[0.3em] text-[#0052FF] font-semibold mb-4">
              Funciones
            </p>
            <h2 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              Todo lo que necesitas para entender tu dinero.
            </h2>
            <p className="text-xl text-[#475569] leading-relaxed">
              Sin curva de aprendizaje. Sin configuración. Solo abre y registra.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Zap className="w-6 h-6" />}
              title="Registra en segundos"
              description="Añade un ingreso o gasto con un par de clicks. Categorías inteligentes que aprenden de ti."
              testId="feature-quick"
            />
            <FeatureCard
              icon={<PieChart className="w-6 h-6" />}
              title="Visualiza con claridad"
              description="Gráficas limpias, tarjetas de resumen y filtros por mes para ver a dónde se va tu dinero."
              testId="feature-visual"
            />
            <FeatureCard
              icon={<Lightbulb className="w-6 h-6" />}
              title="Recomendaciones inteligentes"
              description="Alertas automáticas cuando hay déficit, gastos altos o oportunidades de ahorro."
              testId="feature-recommendations"
            />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────── HOW IT WORKS ────────────────────────────────────────── */}
      <section id="how" className="py-24 lg:py-32 bg-white" data-testid="landing-how">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-16">
            <p className="text-sm uppercase tracking-[0.3em] text-[#0052FF] font-semibold mb-4">
              Cómo funciona
            </p>
            <h2 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
              Tres pasos. Cero fricción.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            <Step
              number="01"
              title="Crea tu cuenta"
              description="Email y contraseña. Sin formularios eternos."
            />
            <Step
              number="02"
              title="Registra movimientos"
              description="Ingresos y gastos por categoría. Tarda menos que escribir un mensaje."
            />
            <Step
              number="03"
              title="Recibe insights"
              description="Saldo en tiempo real, distribución por categoría y alertas accionables."
            />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────── DASHBOARD PREVIEW ────────────────────────────────────────── */}
      <section id="preview" className="py-24 lg:py-32 bg-[#0F172A] relative overflow-hidden" data-testid="landing-preview">
        <div className="absolute inset-0 opacity-30">
          <div className="finly-hero-orb" style={{ top: "20%", left: "10%" }}></div>
          <div className="finly-hero-orb" style={{ bottom: "10%", right: "10%", animationDelay: "6s" }}></div>
        </div>

        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16 text-white">
            <p className="text-sm uppercase tracking-[0.3em] text-[#00E590] font-semibold mb-4">
              Vista previa
            </p>
            <h2 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              Así se ve tu mes en Finly+.
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto leading-relaxed">
              Un solo vistazo y sabes dónde estás parado.
            </p>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0052FF]/30 to-[#00E590]/30 blur-3xl rounded-3xl"></div>
            <div className="relative">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────── FINAL CTA ────────────────────────────────────────── */}
      <section className="py-24 lg:py-32 bg-white" data-testid="landing-final-cta">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0052FF]/10 mb-8">
            <Sparkles className="w-4 h-4 text-[#0052FF]" />
            <span className="text-sm uppercase tracking-[0.25em] font-semibold text-[#0052FF]">
              Empieza hoy
            </span>
          </div>

          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-8">
            Pon tu dinero en una.
            <br />
            <span className="text-[#0052FF]">Empieza gratis.</span>
          </h2>

          <p className="text-xl sm:text-2xl text-[#475569] mb-10 max-w-2xl mx-auto leading-relaxed">
            Sin tarjeta de crédito. Sin permanencia. Solo claridad financiera.
          </p>

          <Link
            to="/register"
            className="inline-flex items-center gap-2 h-16 px-10 bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold rounded-md text-lg transition-all hover:scale-[1.02]"
            data-testid="final-cta-register"
          >
            Crear cuenta gratis
            <ArrowRight className="w-5 h-5" />
          </Link>

          <p className="mt-6 text-sm text-[#94A3B8]">
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="text-[#0052FF] font-medium hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </section>

      {/* ────────────────────────────────────────── FOOTER ────────────────────────────────────────── */}
      <footer className="border-t border-[#E2E8F0] py-10 bg-white" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <FinlyLogo size={28} rounded="22%" />
            <span className="font-display font-semibold text-sm">Finly+</span>
            <span className="text-xs text-[#94A3B8] ml-2">© 2026 · Tu dinero, en una.</span>
          </div>
          <div className="flex items-center gap-6 text-xs text-[#64748B]">
            <a href="#features" className="hover:text-[#0F172A] transition-colors">Funciones</a>
            <a href="#how" className="hover:text-[#0F172A] transition-colors">Cómo funciona</a>
            <Link to="/terminos" className="hover:text-[#0F172A] transition-colors" data-testid="footer-link-terms">Términos</Link>
            <Link to="/privacidad" className="hover:text-[#0F172A] transition-colors" data-testid="footer-link-privacy">Privacidad</Link>
            <Link to="/login" className="hover:text-[#0F172A] transition-colors">Iniciar sesión</Link>
          </div>
        </div>
        <div
          className="max-w-7xl mx-auto px-6 lg:px-10 mt-6 pt-5 border-t border-[#F1F5F9] flex justify-center"
          data-testid="landing-credit"
        >
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#94A3B8]">
            <span className="h-px w-6 bg-gradient-to-r from-transparent to-[#8A9A5B]/50"></span>
            Un desarrollo más de
            <span className="font-display text-[12px] font-semibold tracking-[0.12em] text-[#5C6B3A] normal-case">
              Jabes Avalúos
            </span>
            <span className="h-px w-6 bg-gradient-to-l from-transparent to-[#8A9A5B]/50"></span>
          </span>
        </div>
      </footer>
    </div>
  );
}

/* ────────────────────────────── Subcomponents ────────────────────────────── */

function FeatureCard({ icon, title, description, testId }) {
  return (
    <div
      className="group p-8 bg-white rounded-2xl border border-[#E2E8F0] hover:border-[#0052FF] hover:shadow-lg transition-all"
      data-testid={testId}
    >
      <div className="w-14 h-14 rounded-xl bg-[#0052FF]/10 text-[#0052FF] flex items-center justify-center mb-6 group-hover:bg-[#0052FF] group-hover:text-white transition-colors">
        {icon}
      </div>
      <h3 className="font-display text-2xl font-semibold mb-3 tracking-tight">{title}</h3>
      <p className="text-base text-[#475569] leading-relaxed">{description}</p>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="relative">
      <div className="font-display text-7xl font-black text-[#0052FF]/10 leading-none mb-4">
        {number}
      </div>
      <h3 className="font-display text-3xl font-semibold mb-3 tracking-tight">{title}</h3>
      <p className="text-lg text-[#475569] leading-relaxed">{description}</p>
    </div>
  );
}

function DashboardMockup({ compact = false }) {
  return (
    <div
      className={`bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] overflow-hidden ${
        compact ? "scale-95 lg:scale-100" : ""
      }`}
      data-testid="dashboard-mockup"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0] bg-[#FAFAFA]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#FF5C5C]"></div>
          <div className="w-2 h-2 rounded-full bg-[#FFB84D]"></div>
          <div className="w-2 h-2 rounded-full bg-[#00E590]"></div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium">
          finly.app/dashboard
        </div>
        <div className="w-12"></div>
      </div>

      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-end justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-medium mb-2">
            Saldo del mes · Febrero 2026
          </p>
          <div className="font-display text-4xl font-black text-[#0052FF]">$12,480.00</div>
          <p className="text-xs text-[#64748B] mt-1">18 movimientos registrados</p>
        </div>
        <div className="hidden sm:flex items-center gap-1.5 h-8 px-3 bg-[#0052FF] text-white text-xs font-medium rounded-md">
          + Nuevo movimiento
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 gap-3 px-6 pb-4">
        <StatCard
          label="Ingresos"
          value="$28,500"
          icon={<TrendingUp className="w-3.5 h-3.5" />}
          color="#0052FF"
          bg="rgba(0, 82, 255, 0.08)"
        />
        <StatCard
          label="Gastos"
          value="$16,020"
          icon={<TrendingDown className="w-3.5 h-3.5" />}
          color="#FF5C5C"
          bg="rgba(255, 92, 92, 0.08)"
        />
        <StatCard
          label="Saldo"
          value="$12,480"
          icon={<Wallet className="w-3.5 h-3.5" />}
          color="#00B873"
          bg="rgba(0, 184, 115, 0.08)"
        />
      </div>

      {/* Chart + recommendations */}
      <div className="grid grid-cols-5 gap-3 px-6 pb-6">
        {/* Donut chart */}
        <div className="col-span-3 p-4 bg-[#FAFAFA] rounded-xl border border-[#E2E8F0]">
          <p className="text-xs font-semibold mb-3">Distribución de gastos</p>
          <div className="flex items-center gap-4">
            <DonutMock />
            <div className="flex-1 space-y-1.5 text-[10px]">
              <LegendItem color="#0052FF" label="Vivienda" value="38%" />
              <LegendItem color="#00E590" label="Comida" value="24%" />
              <LegendItem color="#FFB84D" label="Transporte" value="16%" />
              <LegendItem color="#FF5C5C" label="Ocio" value="12%" />
              <LegendItem color="#94A3B8" label="Otros" value="10%" />
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div className="col-span-2 p-4 bg-[#0052FF]/5 rounded-xl border border-[#0052FF]/20">
          <div className="flex items-center gap-1.5 mb-2">
            <Lightbulb className="w-3 h-3 text-[#0052FF]" />
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#0052FF]">
              Recomendación
            </p>
          </div>
          <p className="text-xs font-semibold mb-1 leading-tight">¡Buen mes!</p>
          <p className="text-[10px] text-[#64748B] leading-snug">
            Estás ahorrando un 44% de tus ingresos. Considera invertir el excedente.
          </p>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon, color, bg }) {
  return (
    <div className="p-3 bg-white rounded-xl border border-[#E2E8F0]">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[9px] uppercase tracking-[0.15em] text-[#94A3B8] font-medium">
          {label}
        </span>
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center"
          style={{ color, backgroundColor: bg }}
        >
          {icon}
        </div>
      </div>
      <div className="font-display text-lg font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function LegendItem({ color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-sm" style={{ backgroundColor: color }}></span>
        <span className="text-[#64748B]">{label}</span>
      </div>
      <span className="font-semibold text-[#0F172A]">{value}</span>
    </div>
  );
}

function DonutMock() {
  // Static SVG donut chart with 5 segments
  return (
    <svg viewBox="0 0 36 36" className="w-24 h-24 -rotate-90">
      <circle cx="18" cy="18" r="15.915" fill="none" stroke="#E2E8F0" strokeWidth="4" />
      {/* Vivienda 38% */}
      <circle
        cx="18"
        cy="18"
        r="15.915"
        fill="none"
        stroke="#0052FF"
        strokeWidth="4"
        strokeDasharray="38 62"
        strokeDashoffset="0"
      />
      {/* Comida 24% */}
      <circle
        cx="18"
        cy="18"
        r="15.915"
        fill="none"
        stroke="#00E590"
        strokeWidth="4"
        strokeDasharray="24 76"
        strokeDashoffset="-38"
      />
      {/* Transporte 16% */}
      <circle
        cx="18"
        cy="18"
        r="15.915"
        fill="none"
        stroke="#FFB84D"
        strokeWidth="4"
        strokeDasharray="16 84"
        strokeDashoffset="-62"
      />
      {/* Ocio 12% */}
      <circle
        cx="18"
        cy="18"
        r="15.915"
        fill="none"
        stroke="#FF5C5C"
        strokeWidth="4"
        strokeDasharray="12 88"
        strokeDashoffset="-78"
      />
      {/* Otros 10% */}
      <circle
        cx="18"
        cy="18"
        r="15.915"
        fill="none"
        stroke="#94A3B8"
        strokeWidth="4"
        strokeDasharray="10 90"
        strokeDashoffset="-90"
      />
    </svg>
  );
}
